import {Injectable, OnDestroy} from '@angular/core';
import {AppRightPaneService, BackdropContainerEnum} from '@mydocma/app-container';
import {BackdropService} from '@mydocma/component-library/src/lib/backdrop';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject} from 'rxjs';
import {MmFaultDetailFormComponent} from '../../mm-fault/mm-fault-detail-form/mm-fault-detail-form.component';
import {MmFaultRightPanePageEnum} from './mm-fault-right-pane.enum';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {PlanEditModeEnum} from '../../mm-plans-view/mm-plan-view/services/plan-edit-mode.enum';
import {MmTranslateService} from '../../../mm-translate.service';
import {FaultsBackdropContainerEnum} from '../../faults-backdrop-container.enum';
import {Router} from '@angular/router';


@UntilDestroy()
@Injectable()
export class MmFaultChangePageService implements OnDestroy {

  changesPerMask: Map<MmFaultRightPanePageEnum, BehaviorSubject<boolean>>;
  private faultDetail: MmFaultDetailFormComponent;


  constructor(
     public readonly appRightPaneService: AppRightPaneService,
     public readonly planEditService: MmPlanEditService,
     private readonly backdropService: BackdropService,
     private readonly translateService: MmTranslateService,
     private readonly dialogService: PromptDialogService,
     private readonly router: Router) {


    this.planEditService.attachTo(this);
    this.changesPerMask = new Map<MmFaultRightPanePageEnum, BehaviorSubject<boolean>>();

    this.changesPerMask.set(MmFaultRightPanePageEnum.fault, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.editFault, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.addActivity, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.editActivity, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.addAttachments, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.plan, new BehaviorSubject<boolean>(false));
    this.changesPerMask.set(MmFaultRightPanePageEnum.fieldConfiguration, new BehaviorSubject<boolean>(false));


    this.backdropService.onBackdropClick
      .pipe(untilDestroyed(this))
      .subscribe(async s => {
        if (!this.router.url.includes('/configurations/structures')) {
          this.showBackdropDialog();
        }
      });

  }

  hasChanges(mask: MmFaultRightPanePageEnum) {
    return this.changesPerMask.get(mask)?.value;
  }

  ngOnDestroy(): void {
  }

  async setChanges(mask: MmFaultRightPanePageEnum, fault?: any) {

    const sub = this.changesPerMask.get(mask);
    if (sub) {
      sub.next(true);
    } else {
      this.changesPerMask.set(mask, new BehaviorSubject<boolean>(true));
    }

    this.appRightPaneService.hideBackdrop();
    if (this.planEditService.planEditMode !== PlanEditModeEnum.None) {
      const hasFaultChanges = !!this.changesPerMask.get(MmFaultRightPanePageEnum.fault)?.value;
      const editingExistingFaultInPlan = !!this.faultDetail?.faultItem && !this.faultDetail.faultItem.isNew;

      const excludes: string[] = [
        // BackdropContainerEnum.MydocmaRightPane,
        BackdropContainerEnum.MydocmaMainPane];
      if (!(editingExistingFaultInPlan && hasFaultChanges)) {
        excludes.push(
          FaultsBackdropContainerEnum.FaultsPlanView,
          FaultsBackdropContainerEnum.FaultsPlanFlyOut,
          FaultsBackdropContainerEnum.FaultsPlanHeader
        );
      }
      this.appRightPaneService.showBackdrop(false, excludes);
    } else {
      const editingExistingFault = (mask === MmFaultRightPanePageEnum.fault)
        && ((fault && !fault.isNew) || (!!this.faultDetail?.faultItem && !this.faultDetail.faultItem.isNew));

      const excludes: string[] = [
        // BackdropContainerEnum.MydocmaRightPane,
        BackdropContainerEnum.MydocmaMainPane
      ];
      if (!editingExistingFault) {
        excludes.push(
          FaultsBackdropContainerEnum.FaultsPlanView,
          FaultsBackdropContainerEnum.FaultsPlanFlyOut,
          FaultsBackdropContainerEnum.FaultsPlanHeader
        );
      }
      this.appRightPaneService.showBackdrop(false, excludes);
    }
  }

  async setPlanSelectionBackdrop(): Promise<void> {
    this.appRightPaneService.hideBackdrop();
    this.backdropService.showBackdrop(BackdropContainerEnum.MydocmaLeftPane);
    this.backdropService.showBackdrop(FaultsBackdropContainerEnum.FaultsHeader);
    this.backdropService.showBackdrop(FaultsBackdropContainerEnum.FaultsChips);
    this.backdropService.showBackdrop(FaultsBackdropContainerEnum.FaultsGrid);
  }

  async disableChanges(mask: MmFaultRightPanePageEnum) {
    const sub = this.changesPerMask.get(mask);
    if (sub) {
      sub.next(false);
    } else {
      this.changesPerMask.set(mask, new BehaviorSubject<boolean>(false));
    }

    let hasChanges = false;
    if (mask === MmFaultRightPanePageEnum.editFault) {
      if (sub.value){
        hasChanges = true;
      }
    }
    else {
      for (const changeOnMask of this.changesPerMask.values()) {
        if (changeOnMask.value) {
          hasChanges = true;
        }
      }
    }

    if (!hasChanges) {
      this.appRightPaneService.hideBackdrop();
    }
  }

  private async showBackdropDialog() {
    const title = await this.translateService.get('MM.dialog.changeDialog.title').toPromise();
    const content = await this.translateService.get('MM.dialog.changeDialog.content').toPromise();

    return (await this.dialogService.showCompleteChangesNotificationDialog({title, content}))?.value;
  }


  setFaultDetail(detailComponent: MmFaultDetailFormComponent) {
    this.faultDetail = detailComponent;
  }

  async getDetailChanges(): Promise<FaultData> {
    return this.faultDetail.getChanges();
  }


}
